import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MobileEditAccount from "../auth/MobileEditAccount";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { collection, getDocs, query, where, doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";

// MUI UI shell while preserving behavior
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus } from "../../utils/dateUtils";
import { useResponsiveFonts, truncateName, truncateText, truncateVehicleModel, truncateVehicleNumber } from "../../utils/responsiveFonts";
import { navigateWithScrollReset, handlePageChangeWithScroll } from "../../utils/ScrollRestoration";
import { fetchClientById } from "../clients/clientSlice";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { firebaseCache } from "../../utils/firebaseCache";
import { clearCurrentPolicy } from "../policies/policySlice";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getPolicyCountsByCategory } from '../../utils/countUtils';
// Icons for bottom navigation
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import FolderIcon from '@mui/icons-material/Folder';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import Fab from '@mui/material/Fab';
import { resolveBranchName } from "../../utils/branchNameResolver";

// Create aliases for bottom navigation
const PolicyIcon = DescriptionIcon;
const BalanceIcon = AccountBalanceWalletIcon;

const MobileClientDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const { clients } = useSelector((state) => state.clients);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const fonts = useResponsiveFonts() || {
    header: { xs: '12px', sm: '14px', md: '16px' },
    title: { xs: '10px', sm: '12px', md: '14px' },
    body: { xs: '8px', sm: '9px', md: '10px' },
    caption: { xs: '4px', sm: '5px', md: '6px' },
    button: { xs: '6px', sm: '7px', md: '8px' }
  };
  const contentRef = useRef(null);
  const isInitialMount = useRef(true);

  // Set isInitialMount to false after initial mount
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Get the page and tab we came from
  const fromPage = location.state?.fromPage || 1;
  const fromTab = location.state?.fromTab || 'customers';
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("motor");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [deletePolicyDialogOpen, setDeletePolicyDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const actionMenuOpen = Boolean(actionMenuAnchor);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const uploadMenuOpen = Boolean(uploadMenuAnchor);
  const [editData, setEditData] = useState({
    name: location.state?.client?.name || "",
    phone: location.state?.client?.mobile || location.state?.client?.phone || "",
    email: location.state?.client?.email || "",
    location: location.state?.client?.address || ""
  });
  const [customerData, setCustomerData] = useState(location.state?.client || null);
  const [displayedPolicies, setDisplayedPolicies] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [policyViewType, setPolicyViewType] = useState('current');

  const itemsPerPage = 5; // Reduced for mobile

  const categories = ["motor", "non-motor", "life"]; // aligns with database structure

  // Function to determine the best default category based on available policies
  const determineDefaultCategory = (policies) => {
    if (!policies || policies.length === 0) return "motor";

    // Check which categories have policies
    const hasMotor = policies.some(p => p.policyCategory === "motor");
    const hasNonMotor = policies.some(p => p.policyCategory === "non-motor");
    const hasLife = policies.some(p => p.policyCategoryType === "life");

    // Priority: motor > non-motor > life
    if (hasMotor) return "motor";
    if (hasNonMotor) return "non-motor";
    if (hasLife) return "life";

    return "motor"; // fallback
  };

  const fetchPoliciesAndCustomer = async () => {
    if (!user?.uid || !customerId || staffLoading) {
      console.log(`⏳ MobileClientDetail: Skipping fetch - user: ${!!user?.uid}, customerId: ${!!customerId}, staffLoading: ${staffLoading}`);
      return;
    }

    try {
      setLoading(true);

      // Determine the correct agentId for this client
      let effectiveAgentId = location.state?.client?.agentId || location.state?.agentId;

      if (!effectiveAgentId && isAdmin && userIds.length > 0) {
        console.log(`🔍 Admin: Searching for customer ${customerId} across ${userIds.length} staff members...`);

        // Check our current clients list from Redux
        const clientInState = clients.find(c => c.id === customerId);
        if (clientInState?.agentId) {
          effectiveAgentId = clientInState.agentId;
          console.log(`✅ Found customer in state, agentId: ${effectiveAgentId}`);
        } else {
          // Search across all staff/admin collections to find where this customer exists
          console.log(`🔍 Admin: Customer not in state, searching across ${userIds.length} agents...`);
          for (const uid of userIds) {
            try {
              const customerRef = doc(db, `agents/${uid}/customers/${customerId}`);
              const customerSnap = await getDoc(customerRef);
              if (customerSnap.exists()) {
                effectiveAgentId = uid;
                console.log(`✅ Found customer in agents/${uid}/customers/${customerId}`);
                break;
              }
            } catch (err) {
              console.warn(`Could not check agents/${uid}/customers/${customerId}:`, err.message);
            }
          }

          if (!effectiveAgentId) {
            console.warn(`⚠️ Admin: Could not find customer ${customerId} in any agent collection. Using first userId as fallback.`);
            effectiveAgentId = userIds[0];
          }
        }
      } else if (!effectiveAgentId) {
        effectiveAgentId = user.uid;
      }

      console.log(`🔄 MobileClientDetail: Fetching for agentId: ${effectiveAgentId}, customerId: ${customerId}`);

      // Use Redux actions for optimized fetching with caching
      await dispatch(fetchClientById({ agentId: effectiveAgentId, customerId }));

      // Fetch policies for this specific customer using optimized query
      const basePath = `agents/${effectiveAgentId}/policies`;
      const q = query(collection(db, basePath), where("customerId", "==", customerId));
      const snapshot = await getDocs(q);
      console.log(`📋 MobileClientDetail: Found ${snapshot.docs.length} policies for customer ${customerId}`);
      const fetchedPolicies = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPolicies(fetchedPolicies);
      console.log(`✅ MobileClientDetail: Set ${fetchedPolicies.length} policies in state`);

      // Auto-select the best category based on available policies
      const bestCategory = determineDefaultCategory(fetchedPolicies);
      setSelectedCategory(bestCategory);

      // Get customer data from Redux state if available, otherwise fallback to Firebase
      const client = clients.find(c => c.id === customerId);
      if (client) {
        setCustomerData(client);
        setEditData({
          name: client.name || "",
          phone: client.mobile || client.phone || "",
          email: client.email || "",
          location: client.address || "",
          referredBy: client.referredBy || ""
        });
      } else {
        // Fallback to direct Firebase query
        const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
        const customerSnap = await getDoc(customerRef);
        if (customerSnap.exists()) {
          const c = customerSnap.data();
          // IMPORTANT: Include agentId so document uploads go to the correct path
          setCustomerData({ id: customerSnap.id, ...c, agentId: effectiveAgentId });
          setEditData({
            name: c.name || "",
            phone: c.mobile || c.phone || "",
            email: c.email || "",
            location: c.address || "",
            referredBy: c.referredBy || ""
          });
        }
      }
    } catch (err) {
      console.error("🔥 Error in fetchPoliciesAndCustomer:", err);
    } finally {
      setLoading(false);
    }
  };

  // Update customerData and editData when clients change or fetch completes
  useEffect(() => {
    const client = clients.find(c => c.id === customerId);
    if (client) {
      setCustomerData(client);
      setEditData({
        name: client.name || "",
        phone: client.mobile || client.phone || "",
        email: client.email || "",
        location: client.address || "",
        referredBy: client.referredBy || ""
      });
    }
  }, [clients, customerId]);

  useEffect(() => {
    console.log(`🔄 MobileClientDetail: useEffect triggered - user: ${user?.uid}, customerId: ${customerId}, staffLoading: ${staffLoading}`);
    if (!staffLoading) {
      fetchPoliciesAndCustomer();
    }
  }, [user?.uid, customerId, dispatch, staffLoading]);

  // Update selected category when policies change (e.g., after adding/updating policies)
  useEffect(() => {
    if (policies.length > 0) {
      const bestCategory = determineDefaultCategory(policies);
      if (bestCategory !== selectedCategory) {
        setSelectedCategory(bestCategory);
      }
    }
  }, [policies]);

  const clientName = customerData?.name || editData.name || "Client";
  const clientPhone = customerData?.mobile || editData.phone || "Phone";

  const handleSaveCustomer = async (updatedData) => {
    try {
      const effectiveAgentId = customerData?.agentId || user?.uid;
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
      // Map the form data to the correct field names for customer
      const updatedPayload = {
        name: updatedData.name,
        mobile: updatedData.phone,
        email: updatedData.email || "",
        address: updatedData.location,
        referredBy: updatedData.referredBy || "",
        updatedAt: new Date().toISOString()
      };
      await setDoc(customerRef, updatedPayload, { merge: true });
      setCustomerData({ ...customerData, ...updatedPayload });
      setEditData(updatedPayload);
      setShowEditModal(false);

      // Update all policies associated with this customer
      try {
        const policiesRef = collection(db, `agents/${effectiveAgentId}/policies`);
        const q = query(policiesRef, where("customerId", "==", customerId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const batch = (await import("firebase/firestore")).writeBatch(db);
          snapshot.docs.forEach(docSnap => {
            const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${docSnap.id}`);
            batch.update(policyRef, {
              name: updatedData.name,
              mobile: updatedData.phone,
              email: updatedData.email || "",
              address: updatedData.location,
              updatedAt: new Date().toISOString()
            });
          });
          await batch.commit();
          console.log(`✅ Updated ${snapshot.docs.length} policies with new customer information`);
        }
      } catch (policyError) {
        console.error("❌ Error updating policies:", policyError);
        // Don't fail the entire operation if policy updates fail
      }

      // Mark client cache for invalidation (ClientDetails write operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      dispatch(clearCurrentPolicy());
      console.log("✅ Client cache marked for invalidation after profile update and current policy cleared");

      // Add a small delay to ensure the modal is closed before showing toast
      setTimeout(() => {
        console.log("🎉 Customer profile update success - showing toast");
        toast.success("Customer profile successfully updated", {
          containerId: "bottom-center",
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
          style: {
            backgroundColor: '#DCFCE7',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: '75vh',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'auto',
            maxWidth: 'none',
            display: 'inline-flex',
          },
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF' }}
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        });
      }, 100);
    } catch (error) {
      console.error("Failed to update customer:", error);
      toast.error("Failed to update customer details");
    }
  };

  // Filter the policies based on current vs previous
  const statusFilteredPolicies = useMemo(() => {
    return policies.filter(p => {
      const status = calculatePolicyStatus(p.expiryDate, p.status);
      return policyViewType === 'current' ? status !== 'Expired' : status === 'Expired';
    });
  }, [policies, policyViewType]);

  const filteredPolicies = useMemo(() => {
    return statusFilteredPolicies.filter((p) => {
      if (selectedCategory === "all") return true;
      if (selectedCategory === "motor") return p.policyCategory === "motor";
      if (selectedCategory === "non-motor") return p.policyCategory === "non-motor";
      if (selectedCategory === "life") return p.policyCategoryType === "life";
      return false;
    });
  }, [statusFilteredPolicies, selectedCategory]);

  // Calculate counts for each category based on statusFilteredPolicies
  const categoryCounts = useMemo(() => {
    return getPolicyCountsByCategory(statusFilteredPolicies);
  }, [statusFilteredPolicies]);

  const sortedPolicies = useMemo(() => {
    return [...filteredPolicies].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });
  }, [filteredPolicies]);

  // Initialize displayed policies when filters change
  useEffect(() => {
    if (sortedPolicies.length > 0) {
      // Show ALL policies initially, not just first page
      setDisplayedPolicies(sortedPolicies);
      setHasMore(false); // No more to load since we're showing all
    } else {
      setDisplayedPolicies([]);
      setHasMore(false);
    }
  }, [selectedCategory, policies.length, policyViewType, sortedPolicies]);

  // Scroll to top when category or view type change
  useEffect(() => {
    if (!isInitialMount.current && contentRef.current) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [selectedCategory, policyViewType]);

  // Infinite scroll handler
  const loadMorePolicies = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      setDisplayedPolicies(prev => {
        const currentLength = prev.length;
        // Recalculate filtered policies inside the callback to get fresh data
        const currentFilteredPolicies = policies.filter((p) => {
          if (selectedCategory === 'All') return true;
          return p.vehicleType === selectedCategory;
        });
        const nextPolicies = currentFilteredPolicies.slice(currentLength, currentLength + itemsPerPage);

        if (nextPolicies.length > 0) {
          setHasMore(currentLength + nextPolicies.length < currentFilteredPolicies.length);
          return [...prev, ...nextPolicies];
        } else {
          setHasMore(false);
          return prev;
        }
      });
      setIsLoading(false);
    }, 300);
  }, [isLoading, hasMore, policies, selectedCategory, itemsPerPage]);

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom && hasMore && !isLoading) {
        loadMorePolicies();
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoading, loadMorePolicies]);

  // Delete customer and related policies
  const handleDeleteCustomer = async () => {
    if (!user?.uid || !customerId) return;
    try {
      const effectiveAgentId = customerData?.agentId || user?.uid;
      // Delete customer document
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
      await (await import("firebase/firestore")).deleteDoc(customerRef);

      // Delete all related policies and their PDF files
      const policiesRef = collection(db, `agents/${effectiveAgentId}/policies`);
      const q = query(policiesRef, where("customerId", "==", customerId));
      const snapshot = await getDocs(q);

      // Delete PDF files from Firebase Storage first
      const { deleteObject, ref } = await import("firebase/storage");
      const { storage } = await import("../../firebase/firebaseConfig");

      const deletePromises = [];
      snapshot.docs.forEach(docSnap => {
        const policyData = docSnap.data();
        // If policy has a PDF URL, delete the file from storage
        if (policyData.pdfUrl) {
          try {
            // Extract the file path from the URL
            const url = new URL(policyData.pdfUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
            if (pathMatch) {
              const filePath = decodeURIComponent(pathMatch[1]);
              const fileRef = ref(storage, filePath);
              deletePromises.push(deleteObject(fileRef));
            }
          } catch (error) {
            console.warn("Could not parse PDF URL for deletion:", policyData.pdfUrl, error);
          }
        }
      });

      // Wait for all PDF deletions to complete
      if (deletePromises.length > 0) {
        await Promise.allSettled(deletePromises);
        console.log(`Attempted to delete ${deletePromises.length} PDF files`);
      }

      // Delete policy documents from Firestore
      const batch = (await import("firebase/firestore")).writeBatch(db);
      snapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
      await batch.commit();

      // Mark cache for invalidation (ClientDetails delete operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      console.log("✅ Client cache marked for invalidation after delete");

      // Show success toast before navigation
      toast.success("Client has been successfully deleted", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });

      // Navigate after a brief delay to ensure toast is visible
      setTimeout(() => {
        navigateWithScrollReset(navigate, '/client', contentRef, {
          currentPage: fromPage,
          selectedTab: fromTab,
          forceRefetch: true // Force refetch to ensure deleted client is removed from list
        });
      }, 500);
    } catch (err) {
      console.error("Error deleting customer and policies:", err);
      toast.error("Failed to delete client. Please try again.");
    }
  };

  // Delete individual policy and its PDF
  const handleDeletePolicy = async (policy) => {
    if (!user?.uid || !policy?.id) return;

    try {
      // Delete PDF file from Firebase Storage first
      if (policy.pdfUrl) {
        try {
          const { deleteObject, ref } = await import("firebase/storage");
          const { storage } = await import("../../firebase/firebaseConfig");

          // Extract the file path from the URL
          const url = new URL(policy.pdfUrl);
          const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
            console.log("✅ PDF file deleted from storage");
          }
        } catch (error) {
          console.warn("Could not delete PDF file:", error);
          // Continue with policy deletion even if PDF deletion fails
        }
      }

      // Delete policy document from Firestore
      const effectiveAgentId = customerData?.agentId || user?.uid;
      const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${policy.id}`);
      await (await import("firebase/firestore")).deleteDoc(policyRef);

      // Update local policies state
      setPolicies(prevPolicies => prevPolicies.filter(p => p.id !== policy.id));

      // Mark cache for invalidation (ClientDetails policy delete operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      dispatch(clearCurrentPolicy());
      console.log("✅ Client cache marked for invalidation after policy delete");

      // Show success toast
      toast.success("Policy has been successfully deleted", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });

    } catch (err) {
      console.error("Error deleting policy:", err);
      toast.error("Failed to delete policy. Please try again.");
    }
  };

  // Process rows with status calculation
  const rows = displayedPolicies.map((p, index) => {
    return {
      id: p.id || index,
      name: clientName,
      mobile: clientPhone,
      policyCategoryType: p.policyCategoryType,
      vehicleNumber: p.vehicleNumber,
      policyType: p.policyType,
      policyCompany: p.policyCompany,
      status: calculatePolicyStatus(p.expiryDate, p.status),
      original: p
    };
  });

  // Document upload logic
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'aadhaar' or 'rc_book'
  const fileInputRef = React.useRef(null);

  const renderDocumentList = (type) => {
    const rawData = customerData?.documents?.[type];
    if (!rawData) return (
      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
        Not uploaded
      </Typography>
    );

    const docList = Array.isArray(rawData) ? rawData : [rawData];

    return (
      <Box sx={{ mt: 1, width: '100%' }}>
        {docList.map((doc, idx) => (
          <Box
            key={doc.id || idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.5,
              borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              <DescriptionIcon sx={{ fontSize: 14, color: '#666' }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  color: '#444',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100px'
                }}
                title={doc.fileName || `Document ${idx + 1}`}
              >
                {doc.fileName || `Document ${idx + 1}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.2 }}>
              <IconButton
                size="small"
                onClick={() => handleViewDocument(doc)}
                sx={{ color: '#0063F7', p: 0.5 }}
                disabled={viewingDocId === (doc.driveFileId || doc.pdfUrl)}
              >
                {viewingDocId === (doc.driveFileId || doc.pdfUrl) ? (
                  <CircularProgress size={16} sx={{ color: '#0063F7' }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setDocToDelete({ ...doc, documentType: type })}
                sx={{ color: '#DC2626', p: 0.5 }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const handleDocumentClick = async (type) => {
    setUploadingDoc(type);
    setIsActionLoading(true);
    try {
      const driveApi = await import('../../utils/driveApi');
      const driveStatus = await driveApi.checkDriveStatus(true); // force refresh
      if (!driveStatus.linked) {
        toast.error("Google drive not linked by admin, pls contact admin", {
          autoClose: 5000,
          containerId: "bottom-center",
          position: "bottom-center"
        });
        return;
      }
      setUploadingDoc(type);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } catch (error) {
      console.error("Error checking Drive status:", error);
      toast.error("Google drive not linked by admin, pls contact admin", {
        autoClose: 5000,
        containerId: "bottom-center",
        position: "bottom-center"
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadingDoc) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error("Please upload a valid PDF, Image, or Word document");
      return;
    }

    try {
      setIsUploading(true);
      setDocUploadProgress(0);

      // Compress file if it's a PDF
      let uploadFile = file;
      if (file.type === 'application/pdf') {
        try {
          setDocUploadProgress(5);
          const { compressPdf } = await import('../../utils/pdfCompressor');
          uploadFile = await compressPdf(file);
          if (uploadFile.size < file.size) {
            console.log(`📦 Document compressed: ${((file.size - uploadFile.size) / 1024).toFixed(0)}KB saved`);
          }
        } catch (compError) {
          console.error('Document compression failed, using original:', compError);
        }
      } else if (file.type.startsWith('image/')) {
        // Option to add image compression here if needed in the future
        console.log('🖼️ Image upload detected, skipping PDF compression');
      }
      setDocUploadProgress(10);

      const driveApi = await import('../../utils/driveApi');

      // Check Drive status
      const driveStatus = await driveApi.checkDriveStatus();
      const driveLinked = driveStatus.linked;

      let uploadResult = {};
      const timestamp = new Date().getTime();
      const extension = file.name.split('.').pop() || 'file';
      const fileName = `${uploadingDoc}_${timestamp}.${extension}`;

      // Use the resolved agentId or fallback more intelligently
      const effectiveAgentId = customerData?.agentId || location.state?.agentId || location.state?.clientId || user?.uid;

      console.log(`📱 [Mobile handleFileChange] Target Agent ID: ${effectiveAgentId}, User: ${user?.uid}, Doc Type: ${uploadingDoc}`);

      if (driveLinked) {
        // Upload to Drive - Simulate progress
        setDocUploadProgress(20);
        // Determine branch name using the resolver
        const isUserAdmin = user?.isPhoneUser || user?.providerData?.some(p => p.providerId === 'phone');
        const branchName = await resolveBranchName(user, effectiveAgentId, isUserAdmin);

        if (!branchName && !isAdmin) {
          toast.error("Please add a Branch Name in your profile before uploading to Google Drive.", { autoClose: 5000 });
          throw new Error("Missing branch name for Drive upload");
        }

        setDocUploadProgress(40);
        const referredBy = customerData?.referredBy || editData?.referredBy || '';
        const clientFolderName = referredBy ? `${clientName} (Ref: ${referredBy})` : clientName;

        setDocUploadProgress(60);
        const driveSubFolder = uploadingDoc === 'rc_book' ? 'Documents/RC' : 'Documents/Aadhar';
        const data = await driveApi.uploadToDrive(uploadFile, branchName, clientFolderName, null, driveSubFolder, fileName);

        setDocUploadProgress(90);
        uploadResult = {
          pdfSource: 'drive',
          driveFileId: data.fileId,
          pdfUrl: data.webViewLink, // Store view URL as fallback
          updatedAt: new Date().toISOString()
        };
        setDocUploadProgress(100);
      } else {
        // Enforce Google Drive - do not allow Firebase Storage uploads for documents
        const errorMsg = "Google drive not linked by admin, pls contact admin";
        toast.error(errorMsg, {
          autoClose: 5000,
          containerId: "bottom-center",
          position: "bottom-center"
        });
        throw new Error("Drive not linked by admin");
      }

      // Update Firestore customer document
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);

      let currentDocs = customerData?.documents?.[uploadingDoc] || [];
      if (currentDocs && !Array.isArray(currentDocs)) {
        currentDocs = [currentDocs];
      }

      const updatedList = [...currentDocs, { ...uploadResult, id: timestamp, fileName: file.name }];

      const updatedDocuments = {
        ...(customerData?.documents || {}),
        [uploadingDoc]: updatedList
      };

      console.log(`💾 [Mobile handleFileChange] Updating Firestore at agents/${effectiveAgentId}/customers/${customerId}`);
      await setDoc(customerRef, { documents: updatedDocuments }, { merge: true });

      setCustomerData({
        ...customerData,
        documents: updatedDocuments
      });

      // Update Redux state local copy - force refresh to bypass cache
      dispatch(fetchClientById({ agentId: effectiveAgentId, customerId, forceRefresh: true }));

      // Invalidate cache using the correct agentId (not user's uid which might be different)
      firebaseCache.invalidateAfterWrite('clientdetails_write', effectiveAgentId);

      // Close uploading dialog before showing success toast
      setIsUploading(false);
      setDocUploadProgress(0);

      toast.success("Customer document successfully added", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } catch (error) {
      console.error("❌ Document upload error:", error);
      toast.error(`Failed to upload ${uploadingDoc}: ${error.message}`);
      setIsUploading(false);
      setDocUploadProgress(0);
    } finally {
      setUploadingDoc(null);
      if (event.target) event.target.value = null;
    }
  };

  const handleDeleteDocument = async (type, docId) => {
    try {
      setIsActionLoading(true);
      const currentDocs = customerData?.documents?.[type];
      if (!currentDocs) return;

      const docList = Array.isArray(currentDocs) ? currentDocs : [currentDocs];
      const docToDelete = docList.find(d => d.id === docId || d.driveFileId === docId || d.pdfUrl === docId);

      if (!docToDelete) {
        toast.error("Document not found");
        setLoading(false);
        return;
      }

      const { storage } = await import('../../firebase/firebaseConfig');
      const { ref, deleteObject } = await import('firebase/storage');
      const { trashDriveFile } = await import('../../utils/driveApi');

      if (docToDelete.pdfSource === 'drive') {
        try {
          await trashDriveFile(docToDelete.driveFileId);
        } catch (driveErr) {
          console.warn("⚠️ Could not delete from Drive (link might be broken), proceeding with Firestore update:", driveErr.message);
        }
      } else if (docToDelete.pdfUrl) {
        // Extract the file path from the URL
        const url = new URL(docToDelete.pdfUrl);
        const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef).catch(err => console.error("Error deleting file from storage:", err));
        }
      }

      // Update Firestore
      const effectiveAgentId = customerData?.agentId || location.state?.agentId || user?.uid;
      console.log(`📱 [Mobile handleDeleteDocument] Target Agent ID: ${effectiveAgentId}, User: ${user?.uid}, Doc Type: ${type}`);
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);

      const updatedList = docList.filter(d => d.id !== docId && d.driveFileId !== docId && d.pdfUrl !== docId);

      const updatedDocuments = { ...customerData.documents };
      if (updatedList.length === 0) {
        delete updatedDocuments[type];
      } else {
        updatedDocuments[type] = updatedList;
      }

      await setDoc(customerRef, { documents: updatedDocuments }, { merge: true });
      setCustomerData({
        ...customerData,
        documents: updatedDocuments
      });

      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      setDocToDelete(null);

      toast.success("Customer document successfully removed", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } catch (error) {
      console.error("❌ Document deletion error:", error);
      toast.error(`Failed to delete document: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (docData) => {
    if (!docData || viewingDocId) return;

    const docId = typeof docData === 'string' ? docData : (docData.driveFileId || docData.pdfUrl);
    setViewingDocId(docId);

    try {
      // Handle legacy string URLs
      if (typeof docData === 'string') {
        window.open(docData, '_blank');
        return;
      }

      if (docData.pdfSource === 'drive') {
        try {
          const driveApi = await import('../../utils/driveApi');
          const viewUrl = await driveApi.getDriveViewUrl(docData.driveFileId);
          if (viewUrl) {
            window.open(viewUrl, '_blank');
          } else if (docData.pdfUrl) {
            window.open(docData.pdfUrl, '_blank');
          } else {
            toast.error("Could not get Drive view URL");
          }
        } catch (error) {
          console.error("❌ Drive view error:", error);
          if (docData.pdfUrl) {
            window.open(docData.pdfUrl, '_blank');
          } else {
            toast.error("Failed to view Drive document. Drive might be unlinked.");
          }
        }
      } else if (docData.pdfUrl) {
        window.open(docData.pdfUrl, '_blank');
      } else if (docData.driveFileId) {
        // Fallback for objects with driveFileId but no pdfSource
        try {
          const driveApi = await import('../../utils/driveApi');
          const viewUrl = await driveApi.getDriveViewUrl(docData.driveFileId);
          window.open(viewUrl, '_blank');
        } catch (error) {
          console.error("❌ Drive view error (fallback):", error);
          toast.error("Failed to view document");
        }
      }
    } catch (error) {
      console.error("❌ View error:", error);
      toast.error("Failed to view document");
    } finally {
      setViewingDocId(null);
    }
  };

  return (
    <Box
      className="mobile-layout"
      sx={{
        width: '100vw',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        overscrollBehavior: 'none'
      }}
    >

      {/* Client card */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 70, sm: 60 },
          px: { xs: 2, sm: 1.5 },
          py: { xs: 1.5, sm: 1.5 },
          backgroundColor: '#0063F7',
          color: '#FFFFFF',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          mx: 0,
          borderRadius: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }}
      >
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#E0EBFF', color: '#0063F7', mr: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
          <PersonIcon sx={{ fontSize: 24 }} />
        </Avatar>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          mr: 2
        }}>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '15px' },
            lineHeight: '1.2',
            letterSpacing: '0.01em',
            opacity: 1,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {clientName}
          </Typography>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: { xs: '12px', sm: '13px' },
            lineHeight: '1.2',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            opacity: 0.9,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>{clientPhone}</Typography>
        </Box>
        {customerData?.referredBy && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            maxWidth: '35%',
            mr: 0.5
          }}>
            <Typography sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: '1.2',
              letterSpacing: '0.01em',
              color: '#FFFFFF',
              opacity: 0.9,
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}>
              Ref: {customerData.referredBy}
            </Typography>
          </Box>
        )}

        {/* Action button with dropdown */}
        <Box sx={{ flexShrink: 0 }}>
          <IconButton
            onClick={(event) => setActionMenuAnchor(event.currentTarget)}
            sx={{
              color: '#FFFFFF',
              p: 1,
              '&:active': {
                transform: 'none',
                backgroundColor: 'transparent'
              },
              transition: 'none',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Menu
            anchorEl={actionMenuAnchor}
            open={actionMenuOpen}
            onClose={() => setActionMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: 120
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                setShowEditModal(true);
              }}
              sx={{
                '&:active': {
                  transform: 'none',
                  backgroundColor: 'rgba(0, 99, 247, 0.05)'
                },
                transition: 'none',
                WebkitTapHighlightColor: 'transparent',
                fontSize: '14px',
                fontFamily: 'Roboto',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <EditIcon sx={{ fontSize: 16, color: '#0063F7' }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                setShowDocumentsDialog(true);
              }}
              sx={{
                '&:active': {
                  transform: 'none',
                  backgroundColor: 'rgba(0, 99, 247, 0.05)'
                },
                transition: 'none',
                WebkitTapHighlightColor: 'transparent',
                fontSize: '14px',
                fontFamily: 'Roboto',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <FilePresentIcon sx={{ fontSize: 16, color: '#10B981' }} />
              View Documents
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                setDeleteDialogOpen(true);
              }}
              sx={{
                '&:active': {
                  transform: 'none',
                  backgroundColor: 'rgba(240, 68, 56, 0.05)'
                },
                transition: 'none',
                WebkitTapHighlightColor: 'transparent',
                fontSize: '14px',
                fontFamily: 'Roboto',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#DC2626'
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
              Delete
            </MenuItem>
          </Menu>
        </Box>
      </Box>


      <Box sx={{
        position: 'fixed',
        top: 70,
        left: 0,
        right: 0,
        zIndex: 999,
        backgroundColor: '#f5f5f5',
        px: 2,
        pt: 2.5, // Increased gap from header
        pb: 1.2,
        borderBottom: 'none'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
          {categories.map((category) => (
            <Box
              key={category}
              onClick={() => setSelectedCategory(category)}
              sx={{
                flex: 1,
                py: 0.6,
                px: 1,
                borderRadius: '15px',
                backgroundColor: selectedCategory === category ? '#E3F2FD' : 'white',
                border: `1px solid ${selectedCategory === category ? '#0063F7' : '#e0e0e0'}`,
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 0,
                transition: 'all 0.2s ease',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Box sx={{
                fontSize: '13.5px !important',
                fontWeight: 500,
                color: selectedCategory === category ? '#0063F7' : '#666',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                {category === 'motor' ? `Motor (${categoryCounts.motor || 0})` :
                  category === 'life' ? `Life (${categoryCounts.life || 0})` :
                    `Health (${categoryCounts['non-motor'] || 0})`}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Policy Cards */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 1,
          pb: 10,
          mt: '138px', // Increased to match top padding
          overflowX: 'hidden',
          backgroundColor: '#f5f5f5'
        }}
      >


        <input
          type="file"
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,image/*"
        />

        {/* Current/Previous Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, gap: 1, mt: 0 }}>
          <Typography sx={{ fontSize: '10px', color: '#6B7280', fontWeight: 500 }}>Showing:</Typography>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            backgroundColor: '#d6dce67c',
            borderRadius: '20px',
            p: '3px',
            height: '32px',
            width: '160px'
          }}>
            <Box sx={{
              position: 'absolute',
              width: 'calc(50% - 3px)',
              height: 'calc(100% - 6px)',
              backgroundColor: '#FFFFFF',
              borderRadius: '17px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: policyViewType === 'current' ? 'translateX(0)' : 'translateX(100%)',
              zIndex: 0
            }} />
            <ToggleButtonGroup
              value={policyViewType}
              exclusive
              onChange={(e, newVal) => {
                if (newVal !== null) {
                  setPolicyViewType(newVal);
                }
              }}
              sx={{
                width: '100%',
                zIndex: 1,
                height: '100%',
                border: 'none',
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  '&:not(:first-of-type)': {
                    marginLeft: 0,
                  }
                },
                '& .MuiToggleButton-root': {
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '17px',
                  border: 'none',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '11px',
                  color: '#6B7280',
                  lineHeight: '1 !important',
                  padding: '0 !important',
                  minHeight: 'unset !important',
                  minWidth: 'unset !important',
                  backgroundColor: 'transparent !important',
                  transition: 'color 0.3s ease',
                  '&.Mui-selected': {
                    color: '#0063F7',
                    backgroundColor: 'transparent !important',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2) !important',
                  }
                }
              }}
            >
              <ToggleButton value="current" disableRipple>Current</ToggleButton>
              <ToggleButton value="previous" disableRipple>Previous</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {loading ? (
          <Typography sx={{ textAlign: 'center', py: 4, fontSize: fonts.body.sm }}>Loading policies...</Typography>
        ) : (
          <>
            {rows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: fonts.body.sm,
                  lineHeight: '1.2',
                  letterSpacing: '0.02em',
                  color: '#6B7280',
                  mb: 1
                }}>
                  {policyViewType === 'current' ? 'No policies found.' : 'No previous policies exists.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {rows.map((row) => (
                  <Paper
                    key={row.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      },
                      '&:active': {
                        transform: 'none',
                        backgroundColor: 'transparent'
                      },
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onClick={() => navigateWithScrollReset(navigate, `/policy/${row.id}`, contentRef, { policy: row.original, fromScreen: 'clientDetail', clientId: customerId, clientFromScreen: 'clients', clientFromPage: fromPage, client: customerData })}
                  >
                    {/* Company Name */}
                    <Typography sx={{
                      fontSize: fonts.body.sm,
                      fontWeight: 600,
                      color: '#0063F7',
                      mb: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {row.policyCompany || 'Insurance Company'}
                    </Typography>

                    {/* 2 Column Layout */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '3fr 1fr',
                      gap: { xs: 0.5, sm: 1 },
                      alignItems: 'flex-start',
                      width: '100%',
                      overflow: 'hidden'
                    }}>
                      {/* Column 1: Policy Details */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          {selectedCategory === "motor" ? (
                            <>
                              <Typography sx={{
                                fontSize: '16px !important',
                                fontWeight: 500,
                                color: '#212121',
                                mb: 0.3,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&.MuiTypography-root': {
                                  fontSize: '16px !important'
                                }
                              }}>
                                {row.vehicleNumber || 'Vehicle'}
                              </Typography>
                              <Typography sx={{
                                fontSize: '14px !important',
                                color: '#757575',
                                lineHeight: 1.1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&.MuiTypography-root': {
                                  fontSize: '14px !important'
                                }
                              }}>
                                {row.policyType || 'Policy Type'}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography sx={{
                                fontSize: '16px !important',
                                fontWeight: 500,
                                color: '#212121',
                                mb: 0.3,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&.MuiTypography-root': {
                                  fontSize: '16px !important'
                                }
                              }}>
                                {row.policyType || 'Policy'}
                              </Typography>
                              <Typography sx={{
                                fontSize: '14px !important',
                                color: '#757575',
                                lineHeight: 1.1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&.MuiTypography-root': {
                                  fontSize: '14px !important'
                                }
                              }}>
                                {row.policyCategoryType || 'Category'}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Column 2: Status with Delete */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}>
                        <StatusBadge
                          status={row.status}
                          size="small"
                        />
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            setPolicyToDelete(row.original);
                            setDeletePolicyDialogOpen(true);
                          }}
                          size="small"
                          sx={{
                            color: '#F04438',
                            '&:hover': {
                              backgroundColor: '#FFF3F2',
                              color: '#D93C32',
                            },
                            p: 0.5
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                      Loading more policies...
                    </Typography>
                  </Box>
                )}


              </Box>
            )}
          </>
        )}
      </Box>



      {
        showEditModal && (
          <MobileEditAccount
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
            }}
            onSave={handleSaveCustomer}
            initialDetails={editData}
            showAvatar={true}
            disabledFields={[]}
            isAgentEdit={false}
          />
        )
      }

      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            width: "90%",
            maxWidth: "400px",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            padding: "20px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: fonts.title.sm,
              lineHeight: "1.2",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to delete this client?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "right",
            padding: "16px",
            marginTop: "16px",
            gap: "8px",
            flexDirection: "row"
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              width: "80px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: fonts.button.sm,
              lineHeight: "1.2",
              letterSpacing: "0.01em",
              textTransform: "none",
              '&:hover': {
                borderColor: "#F04438",
                background: "#FFF3F2",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleDeleteCustomer();
              setDeleteDialogOpen(false);
            }}
            sx={{
              width: "80px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438",
              color: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: fonts.button.sm,
              lineHeight: "1.2",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              '&:hover': {
                backgroundColor: "#D93C32",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Delete Confirmation Dialog */}
      <Dialog
        open={deletePolicyDialogOpen}
        onClose={() => {
          setDeletePolicyDialogOpen(false);
          setPolicyToDelete(null);
        }}
        PaperProps={{
          sx: {
            width: "90%",
            maxWidth: "400px",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            padding: "20px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: fonts.title.sm,
              lineHeight: "1.2",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to delete this policy?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "right",
            padding: "16px",
            marginTop: "16px",
            gap: "8px",
            flexDirection: "row"
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDeletePolicyDialogOpen(false);
              setPolicyToDelete(null);
            }}
            sx={{
              width: "80px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: fonts.button.sm,
              lineHeight: "1.2",
              letterSpacing: "0.01em",
              textTransform: "none",
              '&:hover': {
                borderColor: "#F04438",
                background: "#FFF3F2",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleDeletePolicy(policyToDelete);
              setDeletePolicyDialogOpen(false);
              setPolicyToDelete(null);
            }}
            sx={{
              width: "80px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438",
              color: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: fonts.button.sm,
              lineHeight: "1.2",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              '&:hover': {
                backgroundColor: "#D93C32",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upload FAB */}
      <Fab
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          setUploadMenuAnchor(e.currentTarget);
        }}
        disabled={isActionLoading}
        sx={{
          position: 'fixed',
          bottom: '24px !important',
          right: 16,
          zIndex: 1001,
          backgroundColor: '#0063F7 !important',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(0, 99, 247, 0.4)',
          '&:hover': {
            backgroundColor: '#0052CC !important',
            boxShadow: '0 6px 16px rgba(0, 99, 247, 0.5)',
          }
        }}
      >
        <AddIcon />
      </Fab>

      {/* Upload Menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={uploadMenuOpen}
        onClose={() => setUploadMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            minWidth: 180,
            mb: 1
          }
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            setUploadMenuAnchor(null);
            handleDocumentClick('aadhaar');
          }}
          disabled={isActionLoading}
          sx={{
            py: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: '14px',
            fontFamily: 'Roboto'
          }}
        >
          {isActionLoading && uploadingDoc === 'aadhaar' ? (
            <CircularProgress size={20} sx={{ color: '#0063F7' }} />
          ) : (
            <ContactPageIcon sx={{ fontSize: 20, color: '#0063F7' }} />
          )}
          Upload Aadhar
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            setUploadMenuAnchor(null);
            handleDocumentClick('rc_book');
          }}
          disabled={isActionLoading}
          sx={{
            py: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: '14px',
            fontFamily: 'Roboto'
          }}
        >
          {isActionLoading && uploadingDoc === 'rc_book' ? (
            <CircularProgress size={20} sx={{ color: '#0063F7' }} />
          ) : (
            <DirectionsCarFilledIcon sx={{ fontSize: 20, color: '#0063F7' }} />
          )}
          Upload RC
        </MenuItem>
      </Menu>

      {/* Uploading Dialog */}
      <Dialog
        open={isUploading}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 3,
            width: 'calc(100% - 64px)',
            maxWidth: '300px',
            textAlign: 'center'
          }
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontSize: '18px' }}>Uploading...</Typography>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={docUploadProgress}
            sx={{
              width: "100%",
              height: "8px",
              borderRadius: "10px",
              backgroundColor: "#E5E7EB",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#0063F7"
              }
            }}
          />
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              color: "#374151"
            }}
          >
            {docUploadProgress}%
          </Typography>
        </Box>
      </Dialog>

      {/* View Documents Dialog with In-Dialog Delete Confirmation */}
      <Dialog
        open={showDocumentsDialog}
        onClose={() => {
          setShowDocumentsDialog(false);
          setDocToDelete(null);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            m: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {docToDelete ? "Confirm Delete" : "Documents"}
          </Typography>
          <IconButton onClick={() => {
            setShowDocumentsDialog(false);
            setDocToDelete(null);
          }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          {docToDelete ? (
            <Box sx={{ py: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "18px",
                  lineHeight: "1.4",
                  color: "#212121",
                  mb: 3
                }}
              >
                Are you sure you want to delete this {docToDelete.documentType === 'aadhaar' ? 'Aadhar' : 'RC'} document?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setDocToDelete(null)}
                  sx={{
                    borderRadius: "8px",
                    borderColor: "#F04438",
                    color: "#F04438",
                    textTransform: "none",
                    height: "44px",
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: "#F04438",
                      background: "#FFF3F2",
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={isActionLoading}
                  onClick={() => handleDeleteDocument(docToDelete.documentType, docToDelete.id || docToDelete.driveFileId || docToDelete.pdfUrl)}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#F04438 !important",
                    color: "#fff !important",
                    textTransform: "none",
                    height: "44px",
                    fontWeight: 500,
                    boxShadow: "none",
                    '&:hover': {
                      backgroundColor: "#D93C32 !important",
                    }
                  }}
                >
                  {isActionLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Aadhaar Card</Typography>
                {renderDocumentList('aadhaar')}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>RC Book</Typography>
                {renderDocumentList('rc_book')}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box >
  );
};

export default MobileClientDetail;
